import { useEffect, useMemo, useState } from 'react';
import {
  Mail,
  ChevronDown,
  ChevronRight,
  Search,
  Send,
  Save,
  X,
  CheckCircle2,
  XCircle,
  Loader2,
  Store,
  Eye,
  Paperclip,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { MultiSelect } from '@/components/ui/multi-select';
import { Modal } from '@/components/ui/modal';
import { DateInput } from '@/components/ui/date-input';
import { Button } from '@/components/ui/button';
import { toast } from '@/stores/toast-store';
import { formatDateTime } from '@/lib/utils';
import { useAdminTenants } from '@/hooks/use-admin';
import {
  useReportMailingSettings,
  useReportMailingOptions,
  useUpsertReportMailingSetting,
  useSendTestReportMail,
  useReportMailingPreview,
  type ReportMailingPreview,
  type ReportMailingSetting,
  type ReportMailingOption,
  type ReportMailingUpsert,
} from '@/hooks/use-report-mailing';

// Supported timezones (store separately; cron respects this). Extend as needed.
const TIMEZONES = ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface AdminTenant {
  id: string;
  name: string;
  locations?: { id: string; name: string }[];
}

// ── Multi-email chip input (comma / Enter to add) ──
function MultiEmailInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');

  const commit = (raw: string) => {
    const parts = raw
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const bad = parts.find((p) => !EMAIL_RE.test(p));
    if (bad) {
      setError(`Invalid email: ${bad}`);
      return;
    }
    const merged = Array.from(new Set([...value, ...parts]));
    onChange(merged);
    setDraft('');
    setError('');
  };

  return (
    <div>
      <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-lg border border-input bg-white px-2 py-1.5 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
        {value.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
          >
            {email}
            <button
              type="button"
              onClick={() => onChange(value.filter((e) => e !== email))}
              className="text-primary/70 hover:text-primary"
              aria-label={`Remove ${email}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          placeholder={value.length === 0 ? placeholder : ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v.endsWith(',')) commit(v.slice(0, -1));
            else {
              setDraft(v);
              setError('');
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit(draft);
            } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={() => draft.trim() && commit(draft)}
          className="min-w-[140px] flex-1 border-none bg-transparent text-sm outline-none"
        />
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'h-9 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary';

interface StoreFormState {
  /** Reports sent on this schedule, one email each. */
  reportTypes: string[];
  enabled: boolean;
  timezone: string;
  scheduleTime: string;
  fromEmail: string;
  toEmails: string[];
  ccEmails: string[];
  bccEmails: string[];
  exportFormat: string;
}

/**
 * The store's saved rows → one form. The schedule and recipients are saved
 * identically on every row, so any row can supply them; the report list is the
 * set of rows. A store with no rows gets the defaults and no reports selected.
 */
function settingsToForm(
  settings: Map<string, ReportMailingSetting>,
  defaults: { reportTypes: string[]; exportFormat: string },
): StoreFormState {
  const rows = Array.from(settings.values());
  const setting = rows[0];
  return {
    reportTypes: rows.length ? rows.map((r) => r.reportType) : defaults.reportTypes,
    enabled: setting?.enabled ?? false,
    timezone: setting?.timezone ?? 'America/Los_Angeles',
    scheduleTime: setting?.scheduleTime ?? '22:00',
    fromEmail: setting?.fromEmail ?? '',
    toEmails: setting?.toEmails ?? [],
    ccEmails: setting?.ccEmails ?? [],
    bccEmails: setting?.bccEmails ?? [],
    exportFormat: setting?.exportFormat ?? defaults.exportFormat,
  };
}

function StatusPill({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700',
      ].join(' ')}
    >
      <span className={['h-1.5 w-1.5 rounded-full', enabled ? 'bg-emerald-500' : 'bg-red-500'].join(' ')} />
      {enabled ? 'Active' : 'Disabled'}
    </span>
  );
}

function LastStatusChip({ status }: { status: 'success' | 'failed' | null }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  return status === 'success' ? (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
      <CheckCircle2 className="h-3.5 w-3.5" /> Success
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
      <XCircle className="h-3.5 w-3.5" /> Failed
    </span>
  );
}

/** Today's YYYY-MM-DD in an IANA zone (en-CA formats dates that way). */
function todayInZone(tz: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
      new Date(),
    );
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

// ── Email preview: the report rendered for the store, nothing sent or logged ──
function PreviewEmailDialog({
  open,
  onClose,
  tenant,
  location,
  reportOptions,
  exportFormat,
  timezone,
}: {
  open: boolean;
  onClose: () => void;
  tenant: AdminTenant;
  location: { id: string; name: string };
  /** The reports to choose from — the store's selected ones, else all. */
  reportOptions: ReportMailingOption[];
  exportFormat: string;
  timezone: string;
}) {
  const [reportType, setReportType] = useState(reportOptions[0]?.value ?? '');
  const [date, setDate] = useState(() => todayInZone(timezone));
  const [preview, setPreview] = useState<ReportMailingPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const previewMail = useReportMailingPreview();

  // Re-render whenever the dialog opens or the report / day changes. Keeping
  // the previous preview on screen while the next one loads avoids a flash.
  useEffect(() => {
    if (!open || !reportType) return;
    let cancelled = false;
    setError(null);
    previewMail
      .mutateAsync({ tenantId: tenant.id, locationId: location.id, reportType, exportFormat, timezone, date })
      .then((res) => {
        if (!cancelled) setPreview(res.data);
      })
      .catch((e) => {
        if (!cancelled) {
          setPreview(null);
          setError(e instanceof Error ? e.message : 'Could not render the preview.');
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reportType, date, exportFormat, timezone, tenant.id, location.id]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Email preview"
      subtitle={`What ${location.name}'s recipients would get — rendered live from the store's data, nothing is sent.`}
      widthClass="max-w-[760px]"
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[260px] flex-1">
            <Field label="Report">
              <select value={reportType} onChange={(e) => setReportType(e.target.value)} className={inputCls}>
                {reportOptions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div>
            <Field label={`Report day (${timezone})`}>
              <DateInput
                value={date}
                max={todayInZone(timezone)}
                onChange={(e) => e.target.value && setDate(e.target.value)}
                className={`${inputCls} w-44`}
                portal
              />
            </Field>
          </div>
        </div>

        {preview && (
          <div className="rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs">
            <div>
              <span className="text-muted-foreground">Subject: </span>
              <span className="font-medium text-foreground">{preview.subject}</span>
            </div>
            <div className="mt-1 flex items-center gap-1 text-muted-foreground">
              <Paperclip className="h-3 w-3" /> {preview.filename}
            </div>
          </div>
        )}

        {error ? (
          <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>
        ) : preview ? (
          <div className="relative overflow-hidden rounded-lg border border-border bg-[#eef1f4]">
            {previewMail.isPending && (
              <div className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-white/90 px-2 py-1 text-xs text-muted-foreground shadow-sm">
                <Loader2 className="h-3 w-3 animate-spin" /> Updating…
              </div>
            )}
            {/* The HTML is our own server-rendered template; the sandbox still
                keeps scripts and navigation out of the admin page. */}
            <iframe title="Email preview" srcDoc={preview.html} sandbox="" className="h-[65vh] w-full bg-transparent" />
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Rendering preview…
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── One store's mailing config (collapsed summary → expandable editor) ──
// One schedule + recipient list per store, sent for each selected report as
// its own email. Stored as one row per report so each keeps its send history.
function StoreMailingRow({
  tenant,
  location,
  settings,
  reportTypes,
  exportFormats,
}: {
  tenant: AdminTenant;
  location: { id: string; name: string };
  /** This store's saved settings, keyed by report type. */
  settings: Map<string, ReportMailingSetting>;
  reportTypes: ReportMailingOption[];
  exportFormats: ReportMailingOption[];
}) {
  const defaults = {
    // A new store starts with the first report (Sales Summary) pre-selected.
    reportTypes: reportTypes[0] ? [reportTypes[0].value] : [],
    exportFormat: exportFormats[0]?.value ?? 'csv',
  };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<StoreFormState>(() => settingsToForm(settings, defaults));
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const reportLabel = (value: string) => reportTypes.find((r) => r.value === value)?.label ?? value;
  const configured = reportTypes.map((r) => settings.get(r.value)).filter(Boolean) as ReportMailingSetting[];
  // Most recent send across this store's reports, for the summary row.
  const latest = configured.filter((c) => c.lastSentAt).sort((a, b) => (a.lastSentAt! < b.lastSentAt! ? 1 : -1))[0];

  const upsert = useUpsertReportMailingSetting();
  const testMail = useSendTestReportMail();

  const set = <K extends keyof StoreFormState>(key: K, val: StoreFormState[K]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const buildBody = (): ReportMailingUpsert => ({
    tenantId: tenant.id,
    locationId: location.id,
    reportTypes: form.reportTypes,
    exportFormat: form.exportFormat,
    enabled: form.enabled,
    timezone: form.timezone,
    scheduleTime: form.scheduleTime,
    fromEmail: form.fromEmail.trim() || null,
    toEmails: form.toEmails,
    ccEmails: form.ccEmails,
    bccEmails: form.bccEmails,
  });

  const validate = (): string | null => {
    if (form.reportTypes.length === 0) return 'Select at least one report.';
    if (form.enabled && form.toEmails.length === 0) return 'Add at least one To email to enable mailing.';
    if (form.fromEmail.trim() && !EMAIL_RE.test(form.fromEmail.trim())) return 'From email is invalid.';
    return null;
  };

  const doSave = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      await upsert.mutateAsync(buildBody());
      toast.success('Mailing settings saved successfully.');
      setOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save mailing settings.');
    }
  };

  const onSaveClick = () => {
    // Confirm before disabling a currently-active schedule.
    if (configured.some((c) => c.enabled) && !form.enabled) {
      setConfirmDisable(true);
      return;
    }
    void doSave();
  };

  const onTest = async () => {
    if (form.toEmails.length === 0) {
      toast.error('Add at least one To email to send a test.');
      return;
    }
    if (form.fromEmail.trim() && !EMAIL_RE.test(form.fromEmail.trim())) {
      toast.error('From email is invalid.');
      return;
    }
    if (form.reportTypes.length === 0) {
      toast.error('Select at least one report to send a test.');
      return;
    }
    try {
      const res = await testMail.mutateAsync(buildBody());
      // One email per report; name any that failed so a partial send is visible.
      const failed = res.data.results.filter((r) => r.status === 'failed');
      if (failed.length === 0) {
        toast.success(
          res.data.results.length === 1 ? 'Test email sent.' : `${res.data.results.length} test emails sent.`,
        );
      } else {
        toast.error(`Could not send: ${failed.map((r) => reportLabel(r.reportType)).join(', ')}.`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to send test email.');
    }
  };

  const onCancel = () => {
    setForm(settingsToForm(settings, defaults));
    setOpen(false);
  };

  return (
    <div className="rounded-lg border border-border bg-white">
      {/* Summary row */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <Store className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-[140px] font-medium text-foreground">{location.name}</span>
        {configured.length === 0 ? (
          <span className="text-xs text-muted-foreground">Not configured</span>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {configured.map((c) => (
              <span key={c.reportType} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{reportLabel(c.reportType)}</span>
                <StatusPill enabled={c.enabled} />
                {c.scheduleTime} · {c.timezone}
              </span>
            ))}
          </div>
        )}
        <div className="ml-auto flex items-center gap-4">
          <div className="text-right">
            <div className="text-[11px] uppercase text-muted-foreground/70">Last Sent</div>
            <div className="text-xs text-foreground">
              {latest?.lastSentAt ? formatDateTime(latest.lastSentAt) : '—'}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase text-muted-foreground/70">Last Status</div>
            <LastStatusChip status={latest?.lastStatus ?? null} />
          </div>
          <Button variant="secondary" size="sm" onClick={() => setOpen((o) => !o)}>
            {open ? 'Close' : 'Edit'}
          </Button>
        </div>
      </div>

      {/* Editor */}
      {open && (
        <div className="border-t border-border bg-muted/20 px-4 py-4">
          {/* Which reports go out on this schedule. Each arrives as its own email. */}
          <div className="mb-4 max-w-xl">
            <div className="mb-1 block text-xs font-medium text-muted-foreground">Reports</div>
            <MultiSelect
              options={reportTypes}
              selected={form.reportTypes}
              onChange={(v) => set('reportTypes', v)}
              placeholder="Select the reports to send…"
              searchPlaceholder="Search reports…"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Every selected report is sent as its own email, at the time and to the recipients below.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Enable toggle */}
            <Field label="Enable Mailing">
              <button
                type="button"
                role="switch"
                aria-checked={form.enabled}
                onClick={() => set('enabled', !form.enabled)}
                className={[
                  'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                  form.enabled ? 'bg-primary' : 'bg-gray-200',
                ].join(' ')}
              >
                <span
                  className={[
                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm ring-0 transition-transform',
                    form.enabled ? 'translate-x-5' : 'translate-x-0',
                  ].join(' ')}
                />
              </button>
            </Field>

            <Field label="Sending Time">
              <input
                type="time"
                value={form.scheduleTime}
                onChange={(e) => set('scheduleTime', e.target.value)}
                className={inputCls}
              />
            </Field>

            <Field label="Time Zone">
              <select value={form.timezone} onChange={(e) => set('timezone', e.target.value)} className={inputCls}>
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Export Format">
              <select
                value={form.exportFormat}
                onChange={(e) => set('exportFormat', e.target.value)}
                className={inputCls}
              >
                {exportFormats.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="From Email">
              <input
                type="email"
                value={form.fromEmail}
                placeholder="reports@qpaymentz.com"
                onChange={(e) => set('fromEmail', e.target.value)}
                className={inputCls}
              />
            </Field>

            <div className="lg:col-span-3 md:col-span-2">
              <Field label="To Email(s)">
                <MultiEmailInput
                  value={form.toEmails}
                  onChange={(v) => set('toEmails', v)}
                  placeholder="john@test.com, finance@test.com"
                />
              </Field>
            </div>
            <div className="md:col-span-1 lg:col-span-1">
              <Field label="CC Email(s)">
                <MultiEmailInput value={form.ccEmails} onChange={(v) => set('ccEmails', v)} placeholder="cc@test.com" />
              </Field>
            </div>
            <div className="md:col-span-1 lg:col-span-2">
              <Field label="BCC Email(s)">
                <MultiEmailInput
                  value={form.bccEmails}
                  onChange={(v) => set('bccEmails', v)}
                  placeholder="bcc@test.com"
                />
              </Field>
            </div>
          </div>

          {/* Each report sends separately, so a failure is reported per report. */}
          {configured
            .filter((c) => c.lastStatus === 'failed' && c.lastError)
            .map((c) => (
              <p key={c.reportType} className="mt-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
                Last error ({reportLabel(c.reportType)}): {c.lastError}
              </p>
            ))}

          <div className="mt-4 flex items-center gap-2">
            <Button variant="primary" size="sm" onClick={onSaveClick} disabled={upsert.isPending}>
              {upsert.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save
            </Button>
            <Button variant="secondary" size="sm" onClick={onTest} disabled={testMail.isPending}>
              {testMail.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send Test Email
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setPreviewOpen(true)}>
              <Eye className="h-3.5 w-3.5" />
              Preview Email
            </Button>
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {previewOpen && (
        <PreviewEmailDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          tenant={tenant}
          location={location}
          reportOptions={
            form.reportTypes.length ? reportTypes.filter((r) => form.reportTypes.includes(r.value)) : reportTypes
          }
          exportFormat={form.exportFormat}
          timezone={form.timezone}
        />
      )}

      <ConfirmDialog
        open={confirmDisable}
        title="Disable scheduled mailing?"
        message={`This will stop the automatic report emails for ${location.name}. You can re-enable them anytime.`}
        confirmLabel="Disable"
        variant="warning"
        loading={upsert.isPending}
        onConfirm={async () => {
          setConfirmDisable(false);
          await doSave();
        }}
        onCancel={() => setConfirmDisable(false)}
      />
    </div>
  );
}

// ── One tenant group (expand to show its stores) ──
function TenantGroup({
  tenant,
  settingsByLocation,
  reportTypes,
  exportFormats,
  defaultOpen,
}: {
  tenant: AdminTenant;
  settingsByLocation: Map<string, Map<string, ReportMailingSetting>>;
  reportTypes: ReportMailingOption[];
  exportFormats: ReportMailingOption[];
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const locations = tenant.locations ?? [];
  const empty = new Map<string, ReportMailingSetting>();
  const activeCount = locations.filter((l) =>
    Array.from((settingsByLocation.get(l.id) ?? empty).values()).some((s) => s.enabled),
  ).length;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/40"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <span className="font-semibold text-foreground">{tenant.name}</span>
        <span className="text-xs text-muted-foreground">
          {locations.length} {locations.length === 1 ? 'store' : 'stores'}
          {activeCount > 0 && <span className="ml-1 text-emerald-600">· {activeCount} active</span>}
        </span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-border bg-muted/10 p-3">
          {locations.length === 0 ? (
            <p className="px-1 py-4 text-center text-sm text-muted-foreground">No stores for this tenant.</p>
          ) : (
            locations.map((loc) => (
              <StoreMailingRow
                key={loc.id}
                tenant={tenant}
                location={loc}
                settings={settingsByLocation.get(loc.id) ?? empty}
                reportTypes={reportTypes}
                exportFormats={exportFormats}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function AdminMailingReportPage() {
  const [search, setSearch] = useState('');
  const tenantsQuery = useAdminTenants({ perPage: 200 });
  const settingsQuery = useReportMailingSettings();
  const optionsQuery = useReportMailingOptions();

  // store → (report type → setting); a store has one row per report.
  const settingsByLocation = useMemo(() => {
    const map = new Map<string, Map<string, ReportMailingSetting>>();
    for (const s of settingsQuery.data?.data ?? []) {
      const byReport = map.get(s.locationId) ?? new Map<string, ReportMailingSetting>();
      byReport.set(s.reportType, s);
      map.set(s.locationId, byReport);
    }
    return map;
  }, [settingsQuery.data]);

  const reportTypes = optionsQuery.data?.data.reportTypes ?? [
    { value: 'sales_summary_current_location', label: 'Sales Summary for Current Location' },
  ];
  const exportFormats = optionsQuery.data?.data.exportFormats ?? [{ value: 'csv', label: 'CSV' }];

  const tenants = useMemo<AdminTenant[]>(() => (tenantsQuery.data?.data ?? []) as AdminTenant[], [tenantsQuery.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants
      .map((t) => {
        const tenantMatch = t.name.toLowerCase().includes(q);
        const locs = (t.locations ?? []).filter((l) => l.name.toLowerCase().includes(q));
        if (tenantMatch) return t; // whole tenant matches → keep all its stores
        if (locs.length > 0) return { ...t, locations: locs };
        return null;
      })
      .filter(Boolean) as AdminTenant[];
  }, [tenants, search]);

  const loading = tenantsQuery.isLoading || settingsQuery.isLoading;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Mailing Report"
        description="Configure automatic daily email delivery of exported reports to tenants, per store, after closing."
      />

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by tenant or store…"
          className="h-9 w-full rounded-lg border border-input bg-white pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border py-16 text-muted-foreground">
          <Mail className="h-6 w-6" />
          <p className="text-sm">No tenants match “{search}”.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((tenant) => (
            <TenantGroup
              key={tenant.id}
              tenant={tenant}
              settingsByLocation={settingsByLocation}
              reportTypes={reportTypes}
              exportFormats={exportFormats}
              defaultOpen={!!search}
            />
          ))}
        </div>
      )}
    </div>
  );
}
